"use client";

import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useTranslations } from "next-intl";
import type { EventType } from "@/app/types/event";
import { Calendar, MapPin } from "lucide-react";
import dayjs from "dayjs";
import { useRouter } from "@/i18n/navigation";
import {
  SliderArrowLeft,
  SliderArrowRight,
} from "@/app/components/common/Icons";

const EventSlider = ({
  data,
  type,
}: {
  data: EventType[];
  type: "flights" | "hotels";
}) => {
  const t = useTranslations();
  const { push } = useRouter();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [mainSwiper, setMainSwiper] = useState<any>(null);

  const mainPrevRef = useRef<HTMLButtonElement>(null);
  const mainNextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Assign refs to mainSwiper navigation
    if (mainSwiper) {
      mainSwiper.params.navigation.prevEl = mainPrevRef?.current;
      mainSwiper.params.navigation.nextEl = mainNextRef?.current;
      mainSwiper?.navigation.init();
      mainSwiper?.navigation.update();
    }
  }, [mainSwiper]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatDate = (checkIn: string, checkOut: string) => {
    return `${dayjs(checkIn).format("D")} - ${dayjs(checkOut).format("D MMM YYYY")}`;
  };

  const isUpcomingEvent = (date: string) => dayjs(date).isAfter(dayjs(), "day");
  return (
    <section className="w-full mt-4">
      <div>
        <div className="flex flex-col items-center gap-6 w-full overflow-hidden relative">
          <div className="absolute top-[50%] -translate-y-1/2 z-10 flex justify-between w-full px-4">
            <button
              ref={mainPrevRef}
              className="p-0 md:p-2 md:rounded-full custom-prev "
            >
              <span className="hidden md:block">
                <SliderArrowLeft />
              </span>
              <span className="block md:hidden">
                <SliderArrowLeft color="white" />
              </span>
            </button>
            <button
              ref={mainNextRef}
              className="p-0 md:p-2 md:rounded-full custom-next "
            >
              <span className="hidden md:block">
                <SliderArrowRight />
              </span>
              <span className="block md:hidden">
                <SliderArrowRight color="white" />
              </span>
            </button>
          </div>
          <Swiper
            modules={[Navigation]}
            navigation={{
              prevEl: mainPrevRef.current,
              nextEl: mainNextRef.current,
            }}
            // navigation={true}
            loop={true}
            slidesPerView="auto"
            spaceBetween={16}
            grabCursor={!isMobile}
            className="!w-full"
          >
            {data.map((card, index: number) => {
              const isUpcoming = isUpcomingEvent(card.checkIn);
              return (
                <SwiperSlide
                  key={`${card.name}-${index}`}
                  className="events-card--width"
                  style={{ flexShrink: 0 }}
                >
                  <div className="bg-white dark:bg-sleap-gray-950 border border-sleap-gray-400 dark:border-sleap-gray-600 rounded-2xl cursor-pointer min-h-[326px]">
                    <div className="w-full h-[160px] rounded-t-lg">
                      <img
                        className="w-full h-full object-fill rounded-t-2xl"
                        src={"image" in card ? card.image : ""}
                        alt={t("Other.visitName", { name: card.name })}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="mb-4">
                        <span
                          className={`text-sleap-gray-950 text-xs font-switzer font-semibold ${isUpcoming ? "bg-sleap-fern-400" : "bg-sleap-gray-50"} px-2 py-1 rounded-lg uppercase`}
                        >
                          {isUpcoming
                            ? t("Events.upcomingEvent")
                            : t("Events.pastEvent")}
                        </span>
                      </div>
                      <h4 className="text-sleap-gray-950 dark:text-white text-xl font-switzer font-bold mb-0 line-clamp-1">
                        {card.name}
                      </h4>

                      <p className="text-sleap-gray-950 dark:text-white text-sm opacity-50 font-switzer font-medium my-2 line-clamp-2 min-h-10">
                        {card.description}
                      </p>
                      <div className="flex gap-x-3 items-center">
                        <div className="flex items-center gap-x-1">
                          <span className="w-5 h-5">
                            <Calendar strokeWidth={2} size={20} />
                          </span>{" "}
                          <p className="mb-0 text-sm font-medium text-sleap-gray-950 font-switzer text-nowrap">
                            {formatDate(card.checkIn, card.checkOut)}
                          </p>
                        </div>
                        <div className="flex items-center gap-x-1">
                          <span className="w-5 h-5">
                            <MapPin strokeWidth={2} size={20} />{" "}
                          </span>{" "}
                          <p className="mb-0 text-sm font-medium text-sleap-gray-950 font-switzer line-clamp-1">
                            {card.city}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          disabled={!isUpcoming}
                          onClick={() => {
                            const params = new URLSearchParams({ type });
                            if (type === "hotels") {
                              params.set(
                                "checkIn",
                                dayjs(card.checkIn).format("YYYY-MM-DD")
                              );
                              params.set(
                                "checkOut",
                                dayjs(card.checkOut).format("YYYY-MM-DD")
                              );
                            }

                            push(`/events/${card.slug}?${params.toString()}`);
                          }}
                          className="bg-sleap-purple-300 disabled:bg-sleap-gray-400 text-white py-2 h-12 px-6 rounded-full w-full text-base font-semibold font-switzer"
                        >
                          {t("Events.buttonText")}
                        </button>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default EventSlider;
